import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateToolTypeMutation, useUpdateToolTypeMutation } from '../../../../hooks/mutations/useToolTypeMutation';
import { useAuth } from '../../../../hooks/useAuth';
import { ToolType } from '../../../../services/apiSLTP/materials/toolsTypes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { crudStatus, getPriceTypeInfos, priceTypeEnum, systemConfigKeyEnum } from '../../../../utils/enums';
import CustomTextInput from '../../../CustomTextInput';
import DropDown from '../../../DropDown';
import ToggleLine from '../../../ToggleLine';
import ModalBody from '../../ModalBody';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';

interface ModalProps {
    toolType: ToolType | null;
    showEditModal: boolean;
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateToolTypeModal: React.FC<ModalProps> = ({ toolType, showEditModal, setShowEditModal, isEditMode = true, refetch }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();

    const [selectedDesignation, setSelectedDesignation] = useState(toolType?.designation || '');
    const [price, setPrice] = useState(typeof toolType?.price === 'number' ? toolType?.price.toString() : '');
    const [priceType, setPriceType] = useState({
        label: capitalizeFirstLetter(t(getPriceTypeInfos(toolType?.priceType || priceTypeEnum.DAY))),
        value: (toolType?.priceType || priceTypeEnum.DAY).toString(),
    });
    const [selectedCrudStatus, setSelectedCrudStatus] = useState(toolType?.crudStatus || crudStatus.OK);
    const [displayOrder, setDisplayOrder] = useState(
        typeof toolType?.displayOrder === 'number' ? toolType?.displayOrder.toString() : ''
    );

    const { mutate: mutateToolType } = useUpdateToolTypeMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error updating toolType:', error.message),
    });

    const { mutate: addToolType } = useCreateToolTypeMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error adding toolType:', error.message),
    });

    const handleActiveChange = (isActive: boolean) => {
        setSelectedCrudStatus(isActive ? crudStatus.OK : crudStatus.INACTIVE);
    };

    const isFormValid = () => {
        return !!(selectedDesignation.length && price.length);
    };

    const handleSubmit = () => {
        if (isEditMode) {
            mutateToolType({
                toolTypeId: toolType?._id || '',
                data: {
                    designation: selectedDesignation,
                    crudStatus: selectedCrudStatus,
                    priceType: parseInt(priceType.value),
                    price: parseFloat(price),
                    displayOrder: !isNaN(parseFloat(displayOrder)) ? parseFloat(displayOrder) : null,
                },
            });
        } else {
            if (priceType)
                addToolType({
                    designation: selectedDesignation,
                    crudStatus: selectedCrudStatus,
                    priceType: parseInt(priceType.value),
                    price: parseFloat(price),
                    displayOrder: !isNaN(parseFloat(displayOrder)) ? parseFloat(displayOrder) : null,
                });
        }
    };

    return (
        <ModalBody
            title={t('tool')}
            subTitle={isEditMode ? t('update') : t('add')}
            closeText={t('cancel')}
            okText={t('validate')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disabledOk={!isFormValid()}
            handleOk={handleSubmit}
        >
            <CustomTextInput value={selectedDesignation} onChange={setSelectedDesignation} label={t('name')} errorText={t('error')} required />
            <ToggleLine checked={selectedCrudStatus === crudStatus.OK} onChange={handleActiveChange} title={capitalizeFirstLetter(t('active'))} />
            <CustomTextInput
                value={price}
                onChange={setPrice}
                label={t('price')}
                errorText={t('error')}
                required
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
            <DropDown
                items={Object.values(priceTypeEnum).map((type) => ({
                    label: capitalizeFirstLetter(t(getPriceTypeInfos(type))),
                    value: type.toString(),
                }))}
                selectedItem={priceType}
                handleChange={(e, value) => {
                    if (value) setPriceType({ value: value?.value, label: value?.label });
                }}
                inputLabel={t('pricing')}
                required
            />
            <CustomTextInput value={displayOrder} onChange={setDisplayOrder} label={t('displayOrder')} type="number"  />
        </ModalBody>
    );
};

export default UpdateToolTypeModal;
